/**
 * Anrufen und Mail schreiben — direkt aus den Kundendaten (Benjamin, 24.09.2026).
 *
 * Eine Nummer ist ein `tel:`-Link: auf dem Telefon wählt ein Tipp sofort.
 * Eine Mailadresse öffnet das Schreibfenster der App (`PostSchreiben`), nicht
 * das Mailprogramm des Geräts: gesendet wird aus dem eigenen Postfach, mit
 * Signatur, und die Mail steht danach im Verlauf des Kunden.
 *
 * Beide stoppen den Klick: sie stehen oft in einer Zeile, die selbst klickbar
 * ist (Kundenliste), und sollen dort nicht zusätzlich den Kunden öffnen.
 */
import { useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { parseKontakte } from "../../lib/kundeAnlage";
import { telLink } from "../../lib/notizen";
import type { Kunde } from "../../lib/types";
import PostSchreiben, { type SchreibZiel } from "../projekt/PostSchreiben";

const halt = (e: MouseEvent) => e.stopPropagation();

/** Alle Mailadressen eines Kunden zum Antippen im Schreibfenster: er selbst, dann Kontaktwege und Ansprechpartner. */
export function kundeMailZiele(kunde: Pick<Kunde, "name" | "email" | "kontakte_json">): SchreibZiel[] {
  const ziele: SchreibZiel[] = [];
  const dazu = (email: string, wer: string) => {
    const e = (email || "").trim();
    if (e && !ziele.some((z) => z.email.toLowerCase() === e.toLowerCase())) ziele.push({ email: e, wer });
  };
  dazu(kunde.email, kunde.name);
  for (const k of parseKontakte(kunde.kontakte_json || "[]")) {
    const person = [k.vorname, k.nachname].filter(Boolean).join(" ") || k.name;
    dazu(k.email, [person, k.role].filter(Boolean).join(" · ") || kunde.name);
  }
  return ziele;
}

/** Erste erreichbare Nummer: der Kunde selbst, sonst ein Kontaktweg oder Ansprechpartner. */
export function kundeHauptnummer(kunde: Pick<Kunde, "phone" | "kontakte_json">): string {
  return (kunde.phone || "").trim()
    || parseKontakte(kunde.kontakte_json || "[]").map((k) => k.phone.trim()).find(Boolean)
    || "";
}

export function TelefonLink({ nummer, className, children }: { nummer: string; className?: string; children?: ReactNode }) {
  const n = (nummer || "").trim();
  if (!n) return null;
  return (
    <a className={className} href={telLink(n)} onClick={halt} title={`${n} anrufen`}>
      {children ?? n}
    </a>
  );
}

type MailProps = {
  email: string;
  /** Wohin die Mail im Verlauf gehört. */
  kundeId: string;
  kundeName?: string;
  /** Weitere Adressen zum Antippen; ohne Angabe nur `email`. */
  ziele?: SchreibZiel[];
  className?: string;
  children?: ReactNode;
};

export function MailKnopf({ email, kundeId, kundeName, ziele, className, children }: MailProps) {
  const [offen, setOffen] = useState(false);
  const e = (email || "").trim();
  if (!e) return null;
  return (
    <>
      <button
        type="button"
        className={className}
        title={`Mail an ${e} schreiben`}
        onClick={(ev) => { ev.stopPropagation(); setOffen(true); }}
      >
        {children ?? e}
      </button>
      {/* Per Portal in den body: das Fenster darf weder in einer Tabellenzeile
          noch in einem <label> hängen (dort käme jeder Klick doppelt an). Der
          Wrapper hält Klicks im React-Baum von der Zeile darunter fern. */}
      {offen && createPortal(
        <div onClick={halt} onMouseDown={halt}>
          <PostSchreiben
            onClose={() => setOffen(false)}
            kundeId={kundeId}
            ziele={ziele && ziele.length ? ziele : [{ email: e, wer: kundeName || e }]}
            an={e}
            titel={kundeName}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
